<script setup>
import { useI18n } from 'vue-i18n'
import DriverPortalLayout from '@/Layouts/DriverPortalLayout.vue'
import { Link } from '@inertiajs/vue3'
import { useCurrency } from '@/composables/useCurrency.js'

defineOptions({ layout: DriverPortalLayout })
const { t } = useI18n()
const { formatMoney, formatDate, formatDateTime } = useCurrency()

defineProps({ payout: { type: Object, required: true } })
</script>

<template>
  <Link href="/payouts" class="back">← {{ t('driver_portal.payouts.title') }}</Link>

  <h1 class="title fw-value tabular-nums">{{ formatMoney(payout.amount_cents) }}</h1>

  <section class="card">
    <dl class="meta-grid">
      <div>
        <dt class="meta-label">{{ t('driver_portal.payouts.date') }}</dt>
        <dd class="meta-value fw-value">{{ formatDateTime(payout.paid_at) }}</dd>
      </div>
      <div>
        <dt class="meta-label">{{ t('driver_portal.payouts.method') }}</dt>
        <dd class="meta-value fw-value">{{ payout.method }}</dd>
      </div>
    </dl>
    <div v-if="payout.notes" class="notes">{{ payout.notes }}</div>
  </section>

  <section class="card">
    <h2 class="card-heading">{{ t('driver_portal.payouts.breakdown') }}</h2>
    <ul class="delivery-list">
      <li v-for="d in payout.deliveries" :key="d.id" class="delivery-row">
        <div class="delivery-row__left">
          <span class="delivery-row__kind">{{ d.kind === 'handover' ? '↗' : '↙' }}</span>
          <div>
            <div class="delivery-row__addr">{{ d.address }}</div>
            <div class="delivery-row__date">{{ formatDate(d.scheduled_for) }}</div>
          </div>
        </div>
        <span class="delivery-row__fee fw-value tabular-nums">{{ formatMoney(d.fee_cents) }}</span>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.back {
  display: inline-block;
  margin-bottom: 0.75rem;
  color: var(--muted);
  text-decoration: none;
  font-size: 0.875rem;
}
.title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 1.25rem;
  color: var(--ok);
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
}
.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin: 0;
}
.meta-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  margin-bottom: 0.2rem;
}
.meta-value {
  display: block;
  font-size: 0.9375rem;
  color: var(--ink);
  margin: 0;
}
.notes {
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: var(--surfaceAlt);
  border-left: 3px solid var(--line);
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: var(--inkSoft);
}
.card-heading {
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  margin: 0 0 0.75rem;
}
.delivery-list { list-style: none; margin: 0; padding: 0; }
.delivery-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--line);
}
.delivery-row:last-child { border-bottom: 0; }
.delivery-row__left { display: flex; align-items: center; gap: 0.6rem; min-width: 0; flex: 1; }
.delivery-row__kind { font-size: 1.1rem; color: var(--accent); flex-shrink: 0; }
.delivery-row__addr { font-size: 0.875rem; font-weight: 500; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.delivery-row__date { font-size: 0.75rem; color: var(--muted); }
.delivery-row__fee { font-size: 0.9375rem; color: var(--ok); flex-shrink: 0; margin-left: 0.75rem; }
</style>
